import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    dashboard: "Dashboard",
    permissions: "Permissions",
    users: "Users",
    auditLogs: "Audit Logs",
    settings: "Settings",
    helpManual: "Help & Manual",
    logout: "Logout",
    
    // Common
    search: "Search",
    create: "Create",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    update: "Update",
    loading: "Loading...",
    noResults: "No results found",
    confirm: "Confirm",
    success: "Success",
    error: "Error",
    warning: "Warning",
    active: "active",
    expired: "expired",
    pending: "pending",
    
    // Login Page
    adminLogin: "Admin Login",
    enterCredentials: "Enter your credentials to access the system",
    username: "Username",
    password: "Password",
    enterUsername: "Enter username",
    enterPassword: "Enter password",
    login: "Login",
    loggingIn: "Logging in...",
    loginSuccess: "Login successful",
    loginFailed: "Login failed",
    
    // Dashboard
    welcomeBack: "Welcome Back",
    dashboardSubtitle: "Here's an overview of your tank permissions",
    totalPermissions: "Total Permissions",
    activeDocuments: "Active Documents",
    expiredDocuments: "Expired Documents",
    pendingDocuments: "Pending Documents",
    recentPermissions: "Recent Permissions",
    viewAll: "View All",
    tanks: "tanks",
    
    // Permissions Page
    permissionsTitle: "Permissions",
    permissionsSubtitle: "Manage tank permissions and documents",
    createPermission: "Create Permission",
    searchPermissions: "Search permissions...",
    noPermissions: "No permissions found",
    createFirstPermission: "Create your first permission to get started",
    tanksAssigned: "tanks assigned",
    created: "Created",
    
    // Create Permission Dialog
    createNewPermission: "Create New Permission",
    permissionNumber: "Permission Number",
    enterPermissionNumber: "Enter permission number",
    tankIds: "Tank IDs (Manual Entry)",
    tankIdsHelper: "Enter tank IDs separated by commas (e.g., T-001, T-002, TANK-A1)",
    tankIdsPlaceholder: "T-001, T-002, T-003, TANK-A1, TANK-B2...",
    totalTanks: "Total tanks",
    creating: "Creating...",
    
    // Edit Permission Dialog
    editPermission: "Edit Permission",
    updating: "Updating...",
    updatePermission: "Update Permission",
    
    // Permission Detail Page
    permissionDetails: "Permission Details",
    backToPermissions: "Back to Permissions",
    documentStatus: "Document Status",
    issueDate: "Issue Date",
    expiryDate: "Expiry Date",
    assignedTanks: "Assigned Tanks",
    uploadDocument: "Upload Document",
    uploadNewDocument: "Upload New Document",
    selectPdfFile: "Select PDF File",
    clickToUpload: "Click to upload or drag and drop",
    pdfOnly: "PDF files only",
    uploading: "Uploading...",
    downloadDocument: "Download Document",
    downloadQrCode: "Download QR Code",
    noDocumentUploaded: "No document uploaded yet",
    uploadFirstDocument: "Upload a PDF document to generate a QR code",
    
    // Users Page
    usersTitle: "Users",
    usersSubtitle: "Manage system users and their roles",
    createUser: "Create User",
    searchUsers: "Search by silver...",
    noUsers: "No users found",
    
    // Create User Dialog
    createNewUser: "Create New User",
    name: "Name",
    employeeId: "Employee ID",
    role: "Role",
    admin: "Admin",
    user: "User",
    enterName: "Enter name",
    enterEmployeeId: "Enter employee ID",
    enterPasswordMin: "Enter password (min 6 characters)",
    
    // Edit User Dialog
    editUser: "Edit User",
    newPassword: "New Password (leave blank to keep current)",
    
    // Audit Logs Page
    auditLogsTitle: "Audit Logs",
    auditLogsSubtitle: "Track all system activities and changes",
    filterByStatus: "Filter by status",
    filterByAction: "Filter by action",
    allStatuses: "All Statuses",
    allActions: "All Actions",
    noAuditLogs: "No audit logs found",
    timestamp: "Timestamp",
    action: "Action",
    details: "Details",
    status: "Status",
    
    // Settings Page
    settingsTitle: "Settings",
    settingsSubtitle: "Manage your account and system settings",
    changePassword: "Change Password",
    updateYourPassword: "Update your account password",
    currentPassword: "Current Password",
    confirmNewPassword: "Confirm New Password",
    enterCurrentPassword: "Enter current password",
    enterNewPassword: "Enter new password (min 6 characters)",
    confirmPassword: "Confirm new password",
    changingPassword: "Changing...",
    passwordChanged: "Password changed successfully",
    passwordsDoNotMatch: "New passwords do not match",
    passwordMinLength: "New password must be at least 6 characters",
    
    // Login Background
    loginBackground: "Login Background Image",
    customizeLoginBg: "Customize the login page background",
    currentBackground: "Current Background",
    uploadNewBackground: "Upload New Background",
    chooseImage: "Choose Image",
    uploadingImage: "Uploading...",
    imageTypes: "JPEG, PNG, or WebP",
    backgroundUpdated: "Login background updated successfully",
    
    // Delete Confirmations
    deleteUserConfirm: "Are you sure you want to delete this user?",
    deletePermissionConfirm: "Are you sure you want to delete this permission? This action cannot be undone.",
    
    // Toasts
    userCreated: "User created successfully",
    userUpdated: "User updated successfully",
    userDeleted: "User deleted successfully",
    permissionCreated: "Permission created successfully",
    permissionUpdated: "Permission updated successfully",
    permissionDeleted: "Permission deleted successfully",
    documentUploaded: "Document uploaded successfully",
    
    // Errors
    accessDenied: "Access denied",
    sessionExpired: "Session expired. Please login again.",
    somethingWentWrong: "Something went wrong",
  },
  ar: {
    // Navigation
    dashboard: "لوحة التحكم",
    permissions: "التصاريح",
    users: "المستخدمون",
    auditLogs: "سجلات المراجعة",
    settings: "الإعدادات",
    helpManual: "المساعدة والدليل",
    logout: "تسجيل الخروج",
    
    // Common
    search: "بحث",
    create: "إنشاء",
    edit: "تعديل",
    delete: "حذف",
    cancel: "إلغاء",
    save: "حفظ",
    update: "تحديث",
    loading: "جاري التحميل...",
    noResults: "لا توجد نتائج",
    confirm: "تأكيد",
    success: "نجاح",
    error: "خطأ",
    warning: "تحذير",
    active: "نشط",
    expired: "منتهي",
    pending: "معلق",
    
    // Login Page
    adminLogin: "تسجيل دخول المسؤول",
    enterCredentials: "أدخل بيانات الاعتماد للوصول إلى النظام",
    username: "اسم المستخدم",
    password: "كلمة المرور",
    enterUsername: "أدخل اسم المستخدم",
    enterPassword: "أدخل كلمة المرور",
    login: "تسجيل الدخول",
    loggingIn: "جاري تسجيل الدخول...",
    loginSuccess: "تم تسجيل الدخول بنجاح",
    loginFailed: "فشل تسجيل الدخول",
    
    // Dashboard
    welcomeBack: "مرحباً بعودتك",
    dashboardSubtitle: "إليك نظرة عامة على تصاريح الخزانات",
    totalPermissions: "إجمالي التصاريح",
    activeDocuments: "المستندات النشطة",
    expiredDocuments: "المستندات المنتهية",
    pendingDocuments: "المستندات المعلقة",
    recentPermissions: "التصاريح الأخيرة",
    viewAll: "عرض الكل",
    tanks: "خزان",
    
    // Permissions Page
    permissionsTitle: "التصاريح",
    permissionsSubtitle: "إدارة تصاريح الخزانات والمستندات",
    createPermission: "إنشاء تصريح",
    searchPermissions: "البحث في التصاريح...",
    noPermissions: "لا توجد تصاريح",
    createFirstPermission: "أنشئ أول تصريح للبدء",
    tanksAssigned: "خزان مخصص",
    created: "تم الإنشاء",
    
    // Create Permission Dialog
    createNewPermission: "إنشاء تصريح جديد",
    permissionNumber: "رقم التصريح",
    enterPermissionNumber: "أدخل رقم التصريح",
    tankIds: "معرفات الخزانات (إدخال يدوي)",
    tankIdsHelper: "أدخل معرفات الخزانات مفصولة بفواصل (مثال: T-001, T-002, TANK-A1)",
    tankIdsPlaceholder: "T-001, T-002, T-003, TANK-A1, TANK-B2...",
    totalTanks: "إجمالي الخزانات",
    creating: "جاري الإنشاء...",
    
    // Edit Permission Dialog
    editPermission: "تعديل التصريح",
    updating: "جاري التحديث...",
    updatePermission: "تحديث التصريح",
    
    // Permission Detail Page
    permissionDetails: "تفاصيل التصريح",
    backToPermissions: "العودة للتصاريح",
    documentStatus: "حالة المستند",
    issueDate: "تاريخ الإصدار",
    expiryDate: "تاريخ الانتهاء",
    assignedTanks: "الخزانات المخصصة",
    uploadDocument: "رفع المستند",
    uploadNewDocument: "رفع مستند جديد",
    selectPdfFile: "اختر ملف PDF",
    clickToUpload: "انقر للرفع أو اسحب وأفلت",
    pdfOnly: "ملفات PDF فقط",
    uploading: "جاري الرفع...",
    downloadDocument: "تحميل المستند",
    downloadQrCode: "تحميل رمز QR",
    noDocumentUploaded: "لم يتم رفع مستند بعد",
    uploadFirstDocument: "ارفع مستند PDF لإنشاء رمز QR",
    
    // Users Page
    usersTitle: "المستخدمون",
    usersSubtitle: "إدارة مستخدمي النظام وأدوارهم",
    createUser: "إنشاء مستخدم",
    searchUsers: "البحث حسب الفضي...",
    noUsers: "لا يوجد مستخدمون",
    
    // Create User Dialog
    createNewUser: "إنشاء مستخدم جديد",
    name: "الاسم",
    employeeId: "رقم الموظف",
    role: "الدور",
    admin: "مسؤول",
    user: "مستخدم",
    enterName: "أدخل الاسم",
    enterEmployeeId: "أدخل رقم الموظف",
    enterPasswordMin: "أدخل كلمة المرور (6 أحرف على الأقل)",
    
    // Edit User Dialog
    editUser: "تعديل المستخدم",
    newPassword: "كلمة مرور جديدة (اتركها فارغة للإبقاء على الحالية)",
    
    // Audit Logs Page
    auditLogsTitle: "سجلات المراجعة",
    auditLogsSubtitle: "تتبع جميع أنشطة النظام والتغييرات",
    filterByStatus: "تصفية حسب الحالة",
    filterByAction: "تصفية حسب الإجراء",
    allStatuses: "جميع الحالات",
    allActions: "جميع الإجراءات",
    noAuditLogs: "لا توجد سجلات مراجعة",
    timestamp: "التوقيت",
    action: "الإجراء",
    details: "التفاصيل",
    status: "الحالة",
    
    // Settings Page
    settingsTitle: "الإعدادات",
    settingsSubtitle: "إدارة حسابك وإعدادات النظام",
    changePassword: "تغيير كلمة المرور",
    updateYourPassword: "تحديث كلمة مرور حسابك",
    currentPassword: "كلمة المرور الحالية",
    confirmNewPassword: "تأكيد كلمة المرور الجديدة",
    enterCurrentPassword: "أدخل كلمة المرور الحالية",
    enterNewPassword: "أدخل كلمة المرور الجديدة (6 أحرف على الأقل)",
    confirmPassword: "تأكيد كلمة المرور الجديدة",
    changingPassword: "جاري التغيير...",
    passwordChanged: "تم تغيير كلمة المرور بنجاح",
    passwordsDoNotMatch: "كلمات المرور الجديدة غير متطابقة",
    passwordMinLength: "يجب أن تكون كلمة المرور الجديدة 6 أحرف على الأقل",
    
    // Login Background
    loginBackground: "صورة خلفية تسجيل الدخول",
    customizeLoginBg: "تخصيص خلفية صفحة تسجيل الدخول",
    currentBackground: "الخلفية الحالية",
    uploadNewBackground: "رفع خلفية جديدة",
    chooseImage: "اختر صورة",
    uploadingImage: "جاري الرفع...",
    imageTypes: "JPEG أو PNG أو WebP",
    backgroundUpdated: "تم تحديث خلفية تسجيل الدخول بنجاح",
    
    // Delete Confirmations
    deleteUserConfirm: "هل أنت متأكد من حذف هذا المستخدم؟",
    deletePermissionConfirm: "هل أنت متأكد من حذف هذا التصريح؟ لا يمكن التراجع عن هذا الإجراء.",
    
    // Toasts
    userCreated: "تم إنشاء المستخدم بنجاح",
    userUpdated: "تم تحديث المستخدم بنجاح",
    userDeleted: "تم حذف المستخدم بنجاح",
    permissionCreated: "تم إنشاء التصريح بنجاح",
    permissionUpdated: "تم تحديث التصريح بنجاح",
    permissionDeleted: "تم حذف التصريح بنجاح",
    documentUploaded: "تم رفع المستند بنجاح",
    
    // Errors
    accessDenied: "تم رفض الوصول",
    sessionExpired: "انتهت الجلسة. يرجى تسجيل الدخول مرة أخرى.",
    somethingWentWrong: "حدث خطأ ما",
  }
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('appLanguage') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('appLanguage', language);
    // Update document direction
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const t = translations[language];
  const isRtl = language === 'ar';

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'ar' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, isRtl }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
